import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { RootStore } from "src/stores/RootStore";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { DELETE_REALESTATE_VISAS, GET_REALESTATE_VISAS, UPSERT_REALESTATE_VISAS } from "src/api/cred";
import { GetRealestateVisas } from "src/api/generated/GetRealestateVisas";
import { IRealestateVisa, IRealestateVisaVisa } from "./InvoiceTypes";
import { IUserToAssign } from "./AssignInvoiceStore";
import { cred_realestatevisas_insert_input } from "src/api/generated/globalTypes";
import { UpsertRealestateVisas, UpsertRealestateVisasVariables } from "src/api/generated/UpsertRealestateVisas";
import { DeleteRealestateVisas, DeleteRealestateVisasVariables } from "src/api/generated/DeleteRealestateVisas";
import i18next from "i18next";
import { MessageType } from "src/components/notifications/Notifier";
import { findDifferencesObjArray } from "src/utils/Object";

export enum STATE {
    INIT,
    LOADING_DATA,
    SAVING,
    RESETTING
}

interface IVisaField {
    currentSearchQuery: string;
    selectedQuery: string;
    selectedUserName: string;
}

type RealestateVisaDataMap = {
    [realestateId: string]: IVisaField[]; // index 0: visa1, index 1: visa2, index 2: visa3
};

export class RealestateVisaStore {
    rootStore: RootStore;

    currentState: STATE = STATE.INIT;

    // Datastructure used for storing the realestate visas from the backend / saving to the db
    realestateVisaData: IRealestateVisaVisa[] = [];
    realestateVisaDataOriginal: IRealestateVisaVisa[] = [];

    // Datastructure used for displaying the realestate visas in the UI
    realestateVisaEntries: IRealestateVisa[] = [];

    currentRealestateVisaUserSearchQuery: string[] = [];
    selectedRealestateVisaUserQueryString: string[] = [];
    selectedRealestateVisaUserName: string[] = [];

    realestateVisaFields: RealestateVisaDataMap = {};

    realestateVisaIdsToDelete: string[] = [];

    fieldsWithChanges?: string[][] = [];

    realestateVisaSearchQuery: string = "";

    triggerFocusNextInput: { isActive: boolean; realestateId: string; visaNumber: number } = {
        isActive: false,
        realestateId: "",
        visaNumber: 0
    };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            currentState: observable,
            realestateVisaData: observable,
            realestateVisaDataOriginal: observable,
            realestateVisaFields: observable,
            realestateVisaEntries: observable,
            currentRealestateVisaUserSearchQuery: observable,
            selectedRealestateVisaUserQueryString: observable,
            selectedRealestateVisaUserName: observable,
            realestateVisaIdsToDelete: observable,
            fieldsWithChanges: observable,
            realestateVisaSearchQuery: observable,
            triggerFocusNextInput: observable,

            init: action,
            setCurrentState: action,
            setRealestateVisaEntries: action,
            setRealestateVisaData: action,
            setRealestateVisaDataOriginal: action,
            loadRealesteVisaData: action,
            setCurrentSearchQuery: action,
            setSelectedQuery: action,
            setSelectedUserName: action,
            initializeVisaFieldsFromEntries: action,
            resetRealestateVisaFields: action,
            resetAllRealestateVisaFields: action,
            resetRealestateVisaDataForVisaRole: action,
            resetRealestateVisaFieldsForVisaRole: action,
            resetRealestateVisaForVisaRole: action,
            isRealestateVisaUserSelected: action,
            updateFieldsWithChanges: action,
            setRealestateVisaSearchQuery: action,
            setTriggerFocusNextInput: action,

            hasChanges: computed,
            filteredRealestateVisaEntries: computed
        });
    }

    init = async () => {
        this.setCurrentState(STATE.LOADING_DATA);

        runInAction(() => {
            this.realestateVisaIdsToDelete = [];
        });

        const realestateSearchStore = this.rootStore.realestateSearchStore;
        const assignInvoiceStore = this.rootStore.assignInvoiceStore;

        await realestateSearchStore.loadRealestateData();
        await assignInvoiceStore.loadUsersToAssign();

        const realestateVisaData = await this.loadRealesteVisaData();

        const realestateVisaEntries: IRealestateVisa[] = realestateSearchStore.realestateData
            .map((realestate) => {
                const realestateVisasForRealestate = realestateVisaData.filter((realestateVisa) => {
                    return realestateVisa.realestateId === realestate.id;
                });

                const visas: IRealestateVisaVisa[] = [];

                if (realestateVisasForRealestate && realestateVisasForRealestate.length > 0) {
                    realestateVisasForRealestate.forEach((realestateVisa) => {
                        visas.push(realestateVisa);
                    });
                }

                const realestateVisa: IRealestateVisa = {
                    realestate: {
                        id: realestate.id,
                        number: realestate.number,
                        name: realestate.name,
                        zip: realestate.zip,
                        city: realestate.city
                    },
                    visas: visas.sort((visaA, visaB) => {
                        return visaA.role.toString().localeCompare(visaB.role.toString());
                    })
                };

                return realestateVisa;
            })
            .sort((realestateVisaA, realestateVisa2) => {
                // order by number ascending
                return realestateVisaA.realestate.number.localeCompare(realestateVisa2.realestate.number);
            });

        this.setRealestateVisaEntries(realestateVisaEntries);

        this.initializeVisaFieldsFromEntries(realestateVisaEntries);

        this.setCurrentState(STATE.INIT);
    };

    setTriggerFocusNextInput(triggerFocusNextInput: boolean, realestateId: string, visaNumber: number) {
        this.triggerFocusNextInput = {
            isActive: triggerFocusNextInput,
            realestateId: realestateId,
            visaNumber: visaNumber
        };
    }

    setCurrentState = (currentState: STATE) => {
        this.currentState = currentState;
    };

    setRealestateVisaData = (realestateVisaData: IRealestateVisaVisa[]) => {
        this.realestateVisaData = realestateVisaData;
    };

    setRealestateVisaDataOriginal = (realestateVisaDataOriginal: IRealestateVisaVisa[]) => {
        this.realestateVisaDataOriginal = realestateVisaDataOriginal;
    };

    setRealestateVisaEntries = (realestateVisaEntries: IRealestateVisa[]) => {
        this.realestateVisaEntries = realestateVisaEntries;
    };

    setCurrentSearchQuery(realestateId: string, visaNumber: number, query: string) {
        const fieldIndex = visaNumber - 1;
        this.realestateVisaFields[realestateId][fieldIndex].currentSearchQuery = query;
    }

    setSelectedQuery(realestateId: string, visaNumber: number, query: string) {
        const fieldIndex = visaNumber - 1;
        this.realestateVisaFields[realestateId][fieldIndex].selectedQuery = query;
    }

    setSelectedUserName(realestateId: string, visaNumber: number, query: string) {
        const fieldIndex = visaNumber - 1;
        this.realestateVisaFields[realestateId][fieldIndex].selectedUserName = query;
    }

    setRealestateVisaSearchQuery = (query: string) => {
        this.realestateVisaSearchQuery = query;
    };

    loadRealesteVisaData = async (): Promise<IRealestateVisaVisa[]> => {
        const { data }: { data: GetRealestateVisas } = await apolloClientInstance.query<GetRealestateVisas>({
            query: GET_REALESTATE_VISAS,
            fetchPolicy: "network-only"
        });

        const realestateVisaData: IRealestateVisaVisa[] = data.realestatevisas.map((realestateVisa) => {
            const realestateVisaEntry: IRealestateVisaVisa = {
                id: realestateVisa.id,
                realestateId: realestateVisa.realestateid,
                role: realestateVisa.role,
                userId: realestateVisa.userid,
                userName1: realestateVisa.user?.name1 ?? "",
                userName2: realestateVisa.user?.name2 ?? ""
            };

            return realestateVisaEntry;
        });

        this.setRealestateVisaData(realestateVisaData);
        this.setRealestateVisaDataOriginal([...realestateVisaData]);

        return realestateVisaData;
    };

    initializeVisaFieldsFromEntries = (realestateVisaEntries: IRealestateVisa[]): void => {
        realestateVisaEntries.forEach((entry) => {
            const realestateId = entry.realestate.id;

            // Initialize the visa fields for this real estate
            if (!this.realestateVisaFields[realestateId]) {
                runInAction(() => {
                    this.realestateVisaFields[realestateId] = [
                        { currentSearchQuery: "", selectedQuery: "", selectedUserName: "" },
                        { currentSearchQuery: "", selectedQuery: "", selectedUserName: "" },
                        { currentSearchQuery: "", selectedQuery: "", selectedUserName: "" }
                    ];
                });
            }

            // Process each visa: compute the index based on role (role 1 → index 0, role 2 → index 1, role 3 → index 2)
            entry.visas.forEach((visa) => {
                const selectedUserName = `${visa.userName1} ${visa.userName2}`.trim();

                this.setCurrentSearchQuery(realestateId, visa.role, selectedUserName);
                this.setSelectedQuery(realestateId, visa.role, selectedUserName);
                this.setSelectedUserName(realestateId, visa.role, selectedUserName);
            });
        });
    };

    getSelectedRealestateVisaUserQueryString = (realestateId: string, visaNumber: number): string => {
        const fieldIndex = visaNumber - 1;
        return this.realestateVisaFields[realestateId][fieldIndex].selectedQuery;
    };

    getCurrentRealestateVisaUserSearchQuery = (realestateId: string, visaNumber: number): string => {
        const fieldIndex = visaNumber - 1;
        return this.realestateVisaFields[realestateId][fieldIndex].currentSearchQuery;
    };

    resetRealestateVisaForVisaRole = (realestateId: string, visaNumber: number) => {
        const realestatveVisaEntryToDelete = this.getRealestateVisaData(realestateId, visaNumber);

        runInAction(() => {
            if (realestatveVisaEntryToDelete && realestatveVisaEntryToDelete.id) {
                this.realestateVisaIdsToDelete.push(realestatveVisaEntryToDelete.id);
            }
        });

        this.resetRealestateVisaDataForVisaRole(realestateId, visaNumber);
        this.resetRealestateVisaFieldsForVisaRole(realestateId, visaNumber);

        this.updateFieldsWithChanges();
    };

    resetRealestateVisaDataForVisaRole = (realestateId: string, visaNumber: number) => {
        const updatedRealestateVisaData = this.realestateVisaData.filter(
            (realestateVisa) => !(realestateVisa.realestateId === realestateId && realestateVisa.role === visaNumber)
        );

        this.setRealestateVisaData(updatedRealestateVisaData);
    };

    resetRealestateVisaFieldsForVisaRole = (realestateId: string, visaNumber: number) => {
        const fieldIndex = visaNumber - 1;
        this.realestateVisaFields[realestateId][fieldIndex].currentSearchQuery = "";
        this.realestateVisaFields[realestateId][fieldIndex].selectedQuery = "";
        this.realestateVisaFields[realestateId][fieldIndex].selectedUserName = "";
    };

    resetRealestateVisaFields = (realestateId: string) => {
        this.realestateVisaFields[realestateId].forEach((field) => {
            field.currentSearchQuery = "";
            field.selectedQuery = "";
            field.selectedUserName = "";
        });
    };

    resetAllRealestateVisaFields = () => {
        Object.keys(this.realestateVisaFields).forEach((realestateId) => {
            this.resetRealestateVisaFields(realestateId);
        });
    };

    isRealestateVisaUserSelected = (realestateId: string, visaNumber: number): boolean => {
        let isRealestateVisaUserSelected = false;

        this.realestateVisaData.forEach((realestateVisa) => {
            if (realestateVisa.realestateId === realestateId && realestateVisa.role === visaNumber) {
                isRealestateVisaUserSelected = true;
            }
        });

        return isRealestateVisaUserSelected;
    };

    getRealestateVisaData = (realestateId: string, visaNumber: number): IRealestateVisaVisa | undefined => {
        return this.realestateVisaData.find((realestateVisa) => {
            return realestateVisa.realestateId === realestateId && realestateVisa.role === visaNumber;
        });
    };

    updateDataWithSelectedRealestateVisaUser = (
        selectedRealestateVisaUser: IUserToAssign,
        realestateId: string,
        visaNumber: number
    ) => {
        // Set or update RealestateVisaUser in datastructure
        const realestateVisa = this.realestateVisaData.find((realestateVisa) => {
            return realestateVisa.realestateId === realestateId && realestateVisa.role === visaNumber;
        });

        if (realestateVisa) {
            runInAction(() => {
                realestateVisa.userId = selectedRealestateVisaUser.userid;
            });
        } else {
            // If a row for this realestate & role was previously removed (pending delete),
            // cancel the pending delete and reattach its id to the new selection
            const originalRealestateVisa = this.realestateVisaDataOriginal.find((visa) => {
                return visa.realestateId === realestateId && visa.role === visaNumber;
            });

            runInAction(() => {
                if (originalRealestateVisa?.id) {
                    this.realestateVisaIdsToDelete = this.realestateVisaIdsToDelete.filter(
                        (realestateVisaId) => realestateVisaId !== originalRealestateVisa.id
                    );
                }

                this.realestateVisaData.push({
                    id: originalRealestateVisa?.id,
                    realestateId: realestateId,
                    role: visaNumber,
                    userId: selectedRealestateVisaUser.userid
                });
            });
        }

        // Update the field-values with the selected RealestateVisaUser
        this.setCurrentSearchQuery(realestateId, visaNumber, selectedRealestateVisaUser.name);
        this.setSelectedQuery(realestateId, visaNumber, selectedRealestateVisaUser.name);
        this.setSelectedUserName(realestateId, visaNumber, selectedRealestateVisaUser.name);

        this.updateFieldsWithChanges();
    };

    updateFieldsWithChanges = () => {
        this.fieldsWithChanges = findDifferencesObjArray(
            this.realestateVisaDataOriginal,
            this.realestateVisaData,
            true
        );
    };

    getAlreadySelectedRealestateVisaUserIds = (realestateId: string): string[] => {
        return this.realestateVisaData
            .filter((realestateVisa) => realestateVisa.realestateId === realestateId)
            .map((realestateVisa) => realestateVisa.userId);
    };

    filteredRealestateVisaUserData(realestateId: string, visaNumber: number): IUserToAssign[] {
        const assignInvoiceStore = this.rootStore.assignInvoiceStore;

        const realestateVisaUsers = assignInvoiceStore.usersToAssignData;
        const alreadyAssignedRealestateUserIds = this.getAlreadySelectedRealestateVisaUserIds(realestateId);

        const realestateVisaUsersNotYetSelectedForRealestate = realestateVisaUsers.filter((realestateVisaUser) => {
            return alreadyAssignedRealestateUserIds.includes(realestateVisaUser.userid) === false;
        });

        let currentRealestateVisaUserSearchQuery =
            this.getCurrentRealestateVisaUserSearchQuery(realestateId, visaNumber) ?? "";

        let selectedRealestateVisaUserQueryString =
            this.getSelectedRealestateVisaUserQueryString(realestateId, visaNumber) ?? "";

        if (currentRealestateVisaUserSearchQuery === selectedRealestateVisaUserQueryString) {
            return realestateVisaUsersNotYetSelectedForRealestate;
        }

        if (currentRealestateVisaUserSearchQuery === "") {
            return realestateVisaUsersNotYetSelectedForRealestate;
        }

        const filteredRealestateVisaUserData = realestateVisaUsersNotYetSelectedForRealestate.filter(
            (realestateVisaUser) => {
                return realestateVisaUser.name
                    .toLowerCase()
                    .includes(currentRealestateVisaUserSearchQuery.toLowerCase());
            }
        );

        return filteredRealestateVisaUserData;
    }

    getRealestateVisaInsertInputQueryVariables = async (): Promise<cred_realestatevisas_insert_input[]> => {
        let realestateVisaInsertObjects: cred_realestatevisas_insert_input[] = [];

        this.realestateVisaData?.forEach((realestateVisa: IRealestateVisaVisa) => {
            let realestateVisaInsertObject: cred_realestatevisas_insert_input = {
                realestateid: realestateVisa.realestateId,
                role: realestateVisa.role,
                userid: realestateVisa.userId
            };

            realestateVisaInsertObjects.push(realestateVisaInsertObject);
        });

        return realestateVisaInsertObjects;
    };

    saveRealestateVisas = async (): Promise<boolean> => {
        this.setCurrentState(STATE.SAVING);

        let wasSuccessfullySaved = true;

        // Deletes have to run before the upsert: a pending delete and an upserted row can target
        // the same DB row (same realestate & role), in which case the delete would remove the row
        // that was just upserted
        if (this.realestateVisaIdsToDelete.length > 0) {
            wasSuccessfullySaved = await this.deleteRealestateVisas(this.realestateVisaIdsToDelete);
        }

        if (wasSuccessfullySaved && this.realestateVisaData.length > 0) {
            const realestateVisaVariables: cred_realestatevisas_insert_input[] =
                await this.getRealestateVisaInsertInputQueryVariables();

            const upsertedRealestateVisaIds = await this.upsertRealestateVisasHasura(realestateVisaVariables);

            wasSuccessfullySaved = upsertedRealestateVisaIds.length > 0;
        }

        if (wasSuccessfullySaved) {
            // Re-sync the local data from the DB so newly inserted rows get their ids
            // (otherwise removing such a row later in the same session cannot delete it)
            await this.loadRealesteVisaData();

            runInAction(() => {
                this.realestateVisaIdsToDelete = [];
                this.fieldsWithChanges = [];
            });

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow_settings.visaworkflow.save_success"),
                MessageType.SUCCESS
            );
        } else {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow_settings.visaworkflow.save_error"),
                MessageType.ERROR
            );
        }

        this.setCurrentState(STATE.INIT);

        return wasSuccessfullySaved;
    };

    upsertRealestateVisasHasura = async (
        realestateVisaInsertObjects: cred_realestatevisas_insert_input[]
    ): Promise<string[]> => {
        let upsertedRealestateVisasIds: string[] = [];

        try {
            const { data: upsertRealestateVisasResult } = await apolloClientInstance.mutate<
                UpsertRealestateVisas,
                UpsertRealestateVisasVariables
            >({
                mutation: UPSERT_REALESTATE_VISAS,
                variables: {
                    realestateVisaObjects: realestateVisaInsertObjects
                }
            });

            if (
                upsertRealestateVisasResult &&
                upsertRealestateVisasResult.upserted_realestatevisa_data &&
                upsertRealestateVisasResult.upserted_realestatevisa_data.returning.length > 0
            ) {
                upsertRealestateVisasResult.upserted_realestatevisa_data.returning.forEach((realestateVisa) => {
                    if (realestateVisa.id) {
                        upsertedRealestateVisasIds.push(realestateVisa.id);
                    }
                });
            }
        } catch (error) {
            console.error("Error upserting realestatevisas: ", error);
        }

        return upsertedRealestateVisasIds;
    };

    deleteRealestateVisas = async (realestateVisaIds: string[]): Promise<boolean> => {
        try {
            await apolloClientInstance.mutate<DeleteRealestateVisas, DeleteRealestateVisasVariables>({
                mutation: DELETE_REALESTATE_VISAS,
                variables: {
                    realestateVisaIds: realestateVisaIds
                }
            });

            return true;
        } catch (error) {
            console.error("Error deleting realestate visas: ", error);

            return false;
        }
    };

    resetRealestateVisaList = async () => {
        this.setCurrentState(STATE.RESETTING);

        this.resetAllRealestateVisaFields();
        await this.init();

        runInAction(() => {
            this.fieldsWithChanges = [];
        });

        this.setCurrentState(STATE.INIT);
    };

    get hasChanges(): boolean {
        let hasChanges = false;

        if (this.fieldsWithChanges && this.fieldsWithChanges.length > 0) {
            this.fieldsWithChanges.forEach((field) => {
                if (field.length > 0) {
                    hasChanges = true;
                }
            });
        }

        return hasChanges;
    }

    get filteredRealestateVisaEntries(): IRealestateVisa[] {
        if (this.realestateVisaSearchQuery === "") {
            return this.realestateVisaEntries;
        }

        return this.realestateVisaEntries.filter((realestateVisa) => {
            const zipCity = `${realestateVisa.realestate.zip} ${realestateVisa.realestate.city}`.trim();

            const userNamesInRealestateVisaEntry: string[] = [""];
            realestateVisa.visas.forEach((visa) => {
                const userName = `${visa.userName1} ${visa.userName2}`.trim();
                userNamesInRealestateVisaEntry.push(userName);
            });
            const userNamesInRealestateVisaEntryString = userNamesInRealestateVisaEntry.join(" ");

            return (
                realestateVisa.realestate.number.toLowerCase().includes(this.realestateVisaSearchQuery.toLowerCase()) ||
                zipCity.toLowerCase().includes(this.realestateVisaSearchQuery.toLowerCase()) ||
                realestateVisa.realestate.name.toLowerCase().includes(this.realestateVisaSearchQuery.toLowerCase()) ||
                userNamesInRealestateVisaEntryString
                    .toLowerCase()
                    .includes(this.realestateVisaSearchQuery.toLowerCase())
            );
        });
    }
}
